import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '@/libs/http';
import type { UserDetails } from '@/types';
import { me } from '@/backend/api/auth/me';

interface AuthContextType {
    user: UserDetails | null;
    isLoading: boolean;
    error: string | null;
    login: (token?: string, idpToken?: string) => Promise<UserDetails>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserDetails | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchUser = async () => {
            // Skip initial fetch on auth routes to prevent unnecessary 401s
            const path = window.location.pathname;
            if (path.includes('/auth/callback') || path.includes('/login') || path === '/m/login' || path.includes('/auth/error')) {
                setIsLoading(false);
                return;
            }

            try {
                const userData = await me();
                setUser(userData);
                setError(null);
            } catch (err: any) {
                // 401 here means anonymous user state; not a hard app error.
                const status = err?.response?.status;
                if (status !== 401) {
                    console.error("Failed to fetch user:", err);
                    setError(err.message || 'Failed to authenticate');
                }
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUser();
    }, []);

    const login = async (token?: string, idpToken?: string): Promise<UserDetails> => {
        try {
            if (token) {
                apiClient.setToken(token, idpToken);
            }
            const userData = await me();
            setUser(userData);
            setError(null);
            return userData;
        } catch (err: any) {
            console.error("Login failed:", err);
            setError(err.message || 'Login failed');
            apiClient.clearToken();
            setUser(null);
            throw err;
        }
    };

    const logout = () => {
        apiClient.clearToken();
        setUser(null);
        setError(null);
        window.location.replace('/auth/login');
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, error, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
